/**
 * Error-shape logic for the local-control PQ CSV downloads: decoding a Blob error
 * body back into what a JSON request would have seen, and recognizing the failures
 * the pages present specially. Lives here rather than next to the endpoints so a
 * page can read an error without importing an endpoint module — `pqClient` stays
 * the only real/mock seam, and these predicates hold on either side of it.
 */

import { errorHandler } from '@/utils/errorHandler'

type ErrorResponse = { status?: number; data?: unknown }

/** As much server-supplied detail as an alert can carry without becoming a document. */
const MAX_DETAIL_LENGTH = 240

/**
 * Axios only parses an error body as JSON when the request asked for JSON. The CSV
 * downloads ask for a Blob, so a 409 / 404 / 500 body arrives as a Blob as well, and
 * `errorHandler` cannot read a message out of one — every failure would surface as
 * the generic "could not download" fallback, whatever the backend said. Decode the
 * Blob back into what a JSON request would have seen (a parsed object, or the raw
 * text) before rethrowing, so the usual error handling applies unchanged.
 */
export async function withDecodedErrorBody<T>(request: () => Promise<T>): Promise<T> {
  try {
    return await request()
  } catch (err) {
    const response = responseOf(err)
    if (response?.data instanceof Blob) {
      let text: string
      try {
        text = await blobToText(response.data)
      } catch {
        // Reading the Blob failed, so rethrow the error being decoded rather than the
        // one decoding it: the status is worth more than an unreadable body, and every
        // status-based branch above would otherwise see no response at all.
        throw err
      }
      try {
        response.data = JSON.parse(text)
      } catch {
        response.data = text
      }
    }
    throw err
  }
}

// `Blob.text()` is the modern API; jsdom (our test runtime) still lacks it, so
// fall back to the FileReader path there.
function blobToText(blob: Blob): Promise<string> {
  if (typeof blob.text === 'function') return blob.text()
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result ?? ''))
    reader.onerror = () => reject(reader.error)
    reader.readAsText(blob)
  })
}

// Structural, like `errorHandler`: an axios error is anything carrying a `response`.
function responseOf(err: unknown): ErrorResponse | undefined {
  if (typeof err !== 'object' || err === null || !('response' in err)) return undefined
  const response = (err as { response?: unknown }).response
  return typeof response === 'object' && response !== null ? (response as ErrorResponse) : undefined
}

/** HTTP status of a failed request, if the failure came from a response at all. */
export function errorStatus(err: unknown): number | undefined {
  return responseOf(err)?.status
}

/**
 * The 404 both CSV downloads answer when there is no blob to send, and nothing else.
 * Only a bodiless 404 means that: one carrying a body came from somewhere other than
 * the handler — a misrouted request, a gateway, a stale id — and whatever it says is
 * worth more to the user than "no data yet" would be.
 */
export function isNoCsvDataError(err: unknown): boolean {
  const response = responseOf(err)
  if (response?.status !== 404) return false
  if (typeof response.data === 'string') return response.data.trim() === ''
  return response.data === undefined
}

/**
 * The 409s the combined-CSV download answers that name resources, so the UI can show
 * them by name. `name` says what is wrong with them:
 *
 * - `RUNS_IN_PROGRESS` — their current run of the test has not finished.
 * - `DATA_UNUSABLE` — their run passed but its data is incomplete: discarded over a
 *   schema conflict, never arrived, or missing a chunk.
 * - `SCHEMA_MISMATCH` — the contributing runs disagree on columns, so every contributing
 *   resource is named; `message` says which columns differ.
 *
 * `DATA_TOO_LARGE` (RES-2583) is the other reason for that status. It names no resources,
 * so it is deliberately not modelled here and reaches `describeCsvDownloadFailure`, which
 * shows the backend's message — as does any `name` added later. So a 409 does *not* imply
 * any of the above; only these names do.
 *
 * Hand-written mirror of `LocalPqCombinedCsvConflict` in `local-pq-api-v1.yaml` — the
 * local-PQ API has no generated types yet, so this declares only the fields the UI reads.
 * It reads `resourceIds`, not the deprecated `inProgressResourceIds`, which the backend
 * fills only for `RUNS_IN_PROGRESS`.
 */
export type CombinedCsvConflict = {
  name: CombinedCsvConflictName
  message: string
  resourceIds: string[]
}

const COMBINED_CSV_CONFLICT_NAMES = ['RUNS_IN_PROGRESS', 'DATA_UNUSABLE', 'SCHEMA_MISMATCH'] as const

type CombinedCsvConflictName = (typeof COMBINED_CSV_CONFLICT_NAMES)[number]

function isCombinedCsvConflictName(name: unknown): name is CombinedCsvConflictName {
  return COMBINED_CSV_CONFLICT_NAMES.includes(name as CombinedCsvConflictName)
}

export function asCombinedCsvConflict(err: unknown): CombinedCsvConflict | undefined {
  const response = responseOf(err)
  if (response?.status !== 409) return undefined
  const data = response.data as { name?: unknown; message?: unknown; resourceIds?: unknown } | undefined
  if (!isCombinedCsvConflictName(data?.name)) return undefined
  // The conflict is only useful if it says which resources are affected: an empty list,
  // or one holding something other than ids, would render a sentence naming no one
  // ("Still running: .", "Affected resources: ."), so it is not one, and the failure goes
  // to the ordinary error handling instead. The backend guarantees a non-empty list of
  // UUIDs for these names.
  const resourceIds = data.resourceIds
  if (!isNonEmptyIdList(resourceIds)) return undefined
  const message = typeof data.message === 'string' ? data.message.trim() : ''
  return { name: data.name, message, resourceIds }
}

function isNonEmptyIdList(value: unknown): value is string[] {
  return Array.isArray(value) && value.length > 0 && value.every((id) => typeof id === 'string' && id.trim() !== '')
}

/**
 * The message to show for a failed CSV download.
 *
 * `errorHandler` hands a string response body to the alert as the entire message, which
 * is what makes the decoded body worth having for a 409 or a 404. It is not always worth
 * showing, though: Spring's default error body carries `"message": ""`, which `??` does
 * not treat as absent, and a gateway answers with an HTML page at any status — so a body
 * that is blank or a document rather than a sentence is dropped for the localized line,
 * and one that is merely long is trimmed to what an alert can hold.
 *
 * A 5xx additionally keeps the localized line in front of the detail: the backend's
 * fallback handler answers with raw exception text, which says nothing a user can act on
 * and would otherwise replace the sentence that does. `AlertContext` renders the newline
 * (`pre-line`).
 */
export function describeCsvDownloadFailure(err: unknown, genericMessage: string): string {
  const detail = errorHandler(err, genericMessage).message.trim()
  if (detail === '' || detail.startsWith('<')) {
    return genericMessage
  }
  const shown = detail.length > MAX_DETAIL_LENGTH ? `${detail.slice(0, MAX_DETAIL_LENGTH)}…` : detail
  if (shown === genericMessage) {
    return genericMessage
  }
  const status = errorStatus(err)
  return status !== undefined && status >= 500 ? `${genericMessage}\n${shown}` : shown
}
